import type { WorkbenchCatalogCard } from "./layoutSchemas";
import type { WorkbenchContent, WorkbenchGroup } from "./contentSchemas";
import { merchantHomeCwmOpportunityTypes } from "../merchantHomeSchemas";

type AvailabilityItem = Pick<WorkbenchContent, "state" | "content_version" | "summary" | "source_refs" | "source_status">;
export type AvailabilityGroup = Pick<WorkbenchGroup, "card_type" | "state"> & { items: readonly AvailabilityItem[] };
export type CatalogAvailability = "result" | "setup" | "other" | "checking";

export interface CatalogAvailabilityEntry<Card extends Pick<WorkbenchCatalogCard, "card_type"> = WorkbenchCatalogCard> {
  card: Card;
  group?: AvailabilityGroup;
  availability: CatalogAvailability;
}

function isQualifiedItem(item: AvailabilityItem) {
  return item.state === "ready" && item.content_version === 2 && Boolean(item.source_status) && item.source_refs.length > 0;
}

function qualifiedItem(group: AvailabilityGroup) {
  if (group.state !== "ready") return undefined;
  return group.items.find(isQualifiedItem);
}

export function workbenchAvailability(group: AvailabilityGroup | undefined, checked: boolean): CatalogAvailability {
  if (!checked || !group) return "checking";
  if (qualifiedItem(group)) return "result";
  if (group.state === "requires_binding" || group.state === "requires_setup") return "setup";
  return "other";
}

const availabilityOrder: Record<CatalogAvailability, number> = { result: 0, setup: 1, other: 2, checking: 3 };

export function sortWorkbenchGroups<T extends AvailabilityGroup>(groups: readonly T[]): T[] {
  return groups.map((group, index) => ({ group, index })).sort((left, right) => {
    const category = availabilityOrder[workbenchAvailability(left.group, true)] - availabilityOrder[workbenchAvailability(right.group, true)];
    if (category) return category;
    return left.index - right.index;
  }).map(entry => entry.group);
}

export function projectWorkbenchRailCards(groups: readonly WorkbenchGroup[], conversationId: string): WorkbenchContent[] {
  // 所有分类都明确为空时，投影为单一空态；不把 27 张空资料哨兵堆进右栏。
  if (groups.length > 0 && groups.every(group => group.state === "empty")) return [];
  return (conversationId ? groups : sortWorkbenchGroups(groups)).flatMap(group => group.items);
}

const merchantHomeOpportunityTypes = new Set<WorkbenchContent["card_type"]>(merchantHomeCwmOpportunityTypes);

export function isMerchantHomeOpportunityCardType(cardType: WorkbenchContent["card_type"]) {
  return merchantHomeOpportunityTypes.has(cardType);
}

export interface MerchantHomeSurfaceProjection {
  allCards: WorkbenchContent[];
  opportunities: WorkbenchContent[];
  railResults: WorkbenchContent[];
  railStatuses: WorkbenchContent[];
}

/**
 * 一份 canonical card-content 数据按首页职责投影；这里只缩小展示面，不删除历史记录。
 * group.items[0] 由后端定义为该类型的当前代表项，浏览器不重新解释更新时间。
 */
export function projectMerchantHomeSurfaces(groups: readonly WorkbenchGroup[]): MerchantHomeSurfaceProjection {
  const allCards = projectWorkbenchRailCards(groups, "conversation");
  const opportunities = groups
    .filter(group => isMerchantHomeOpportunityCardType(group.card_type) && group.state === "ready")
    .flatMap(group => group.items.filter(isQualifiedItem));
  const opportunityTypes = new Set(opportunities.map(card => card.card_type));
  const representatives = sortWorkbenchGroups(groups)
    .filter(group => !opportunityTypes.has(group.card_type))
    .flatMap(group => {
      const card = group.items[0];
      return card ? [{ card, availability: workbenchAvailability(group, true) }] : [];
    });

  return {
    allCards,
    opportunities,
    railResults: representatives.filter(entry => entry.availability === "result").map(entry => entry.card),
    railStatuses: representatives.filter(entry => entry.availability !== "result").map(entry => entry.card),
  };
}

export function partitionWorkbenchCatalog<Card extends Pick<WorkbenchCatalogCard, "card_type">>(cards: readonly Card[], groups: readonly AvailabilityGroup[] | undefined) {
  const byType = new Map(groups?.map(group => [group.card_type, group]));
  const entries = cards.map(card => {
    const group = byType.get(card.card_type);
    return { card, group, availability: workbenchAvailability(group, Boolean(groups)) } satisfies CatalogAvailabilityEntry<Card>;
  });
  return {
    result: entries.filter(entry => entry.availability === "result"),
    setup: entries.filter(entry => entry.availability === "setup"),
    other: entries.filter(entry => entry.availability === "other"),
    checking: entries.filter(entry => entry.availability === "checking"),
  };
}
